import { useEffect, useState } from 'react';
import CacheBuster from 'react-cache-buster';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import packageInfo from '../package.json';
import './App.scss';
import { alertError, ToastComponent } from './components/Toast';
import { setData, setLogout } from './core/redux/app/Actions';
import {
  AuthRoutes,
  ExternalRoute,
  FirstRoute,
} from './core/router/AuthRoutes';
import { PublicRoutes } from './core/router/PublicRoutes';
import { supabase } from './supabaseClient';

export default function App() {
  // const isProduction = process.env.NODE_ENV === 'production';
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [isFirst, setIsFirst] = useState(false);

  useEffect(() => {
    setSession(supabase.auth.session());
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event == 'PASSWORD_RECOVERY') {
        setSession(null);
      } else {
        setSession(session);
      }
    });

    // Cleanup: remove listener on unmount
    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (session && session.user && session.user.email) {
      loadUser(session.user.email);
    }
  }, [session && session.user && session.user.email]);

  const loadUser = async (email) => {
    setLoading(true);

    const { data: users, error } = await supabase
      .from('User')
      .select(
        `
        *,
        UserCompany (
            type,
            company
        )`
      )
      .eq('email', email)
      .is('UserCompany.deleted_at', null);

    if (!error) {
      const user = users[0];
      if (user && user.UserCompany && user.UserCompany.length > 0) {
        // Buscar todas as empresas do usuário
        const companyIds = user.UserCompany.map((uc) => uc.company);

        const { data: companiesData, error: companyError } = await supabase
          .from('Company')
          .select('id, active, name')
          .in('id', companyIds)
          .is('deleted_at', null);

        if (companyError) {
          alertError('Erro ao verificar empresas, tente novamente mais tarde!');
          supabase.auth.signOut();
          dispatch(setLogout({}));
          setLoading(false);
          return;
        }

        // Verificar se há pelo menos uma empresa ativa
        const activeCompanies = companiesData.filter((company) => company.active);

        // super-admin vem do banco (tabela super_admins via helper is_super_admin)
        const { data: superFlag } = await supabase.rpc('is_super_admin');
        const isSuperAdmin = superFlag === true;
        user.isSuperAdmin = isSuperAdmin;

        if (activeCompanies.length === 0 && !isSuperAdmin) {
          alertError(
            'Todas as suas empresas estão inativas. Contate o administrador.'
          );
          supabase.auth.signOut();
          dispatch(setLogout({}));
          setLoading(false);
          return;
        }

        // Reordenar UserCompany para colocar as empresas ativas primeiro
        const activeCompanyIds = activeCompanies.map((c) => c.id);
        user.UserCompany.sort((a, b) => {
          const aActive = activeCompanyIds.includes(a.company);
          const bActive = activeCompanyIds.includes(b.company);
          if (aActive && !bActive) return -1;
          if (!aActive && bActive) return 1;
          return 0;
        });

        dispatch(setData({ user, userCompanies: companiesData }));
        const isFirst = user && !user.isRegistered;
        setIsFirst(isFirst);
      } else {
        alertError('Seu usuário não está associado a nenhuma empresa!');
        supabase.auth.signOut();
        dispatch(setLogout({}));
      }
    } else {
      setSession(null);
      alertError('Erro ao entrar, tente novamente mais tarde!');
    }
    setLoading(false);
  };

  const reloadApp = () => {
    setIsFirst(false);
  };

  return (
    <CacheBuster
      currentVersion={packageInfo.version}
      isEnabled={true} // Fixed: meta.json now uses package.json version instead of UUID
      isVerboseMode={false} // Disable verbose mode to prevent version display on screen
      loadingComponent={
        <div className="spinner-clear">
          <Spinner color="primary" size="sm" />
        </div>
      } //If not pass, nothing appears at the time of new version check.
      // metaFileDirectory={"."} //If public assets are hosted somewhere other than root on your server.
    >
      {loading ? (
        <div className="spinner-clear">
          <Spinner color="primary" size="sm" />
        </div>
      ) : (
        <div>
          {location?.pathname?.includes('/external-budget/') ||
          location?.pathname?.includes('/external-os/') ? (
            <ExternalRoute />
          ) : isFirst ? (
            <FirstRoute reloadApp={reloadApp} />
          ) : session ? (
            <AuthRoutes session={session} />
          ) : (
            <PublicRoutes />
          )}
          <ToastComponent />
        </div>
      )}
    </CacheBuster>
  );
}
